// Setup i18next per la chat utente.
// Lingue prioritarie (CLAUDE.md sez. 6.1): IT, EN, FR, DE, ES, AR, ZH-CN, RO, UK, RU.
// Tutte e 10 popolate (DEBT-011 chiuso). Lingue fuori da questo set cadono su EN.
// Font Noto dinamici per script non latino: arabo, ebraico, CJK, devanagari,
// bengali, thai, cirillico (vedi FONT_LOADERS più sotto).

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import it from './it.json';
import en from './en.json';
import ar from './ar.json';
import fr from './fr.json';
import de from './de.json';
import es from './es.json';
import zh from './zh.json';
import ro from './ro.json';
import uk from './uk.json';
import ru from './ru.json';

export const SUPPORTED_LANGS = [
  'it',
  'en',
  'fr',
  'de',
  'es',
  'ar',
  'zh',
  'ro',
  'uk',
  'ru',
] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

export const RTL_LANGS = new Set<string>(['ar', 'he', 'fa', 'ur']);

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGS] as string[],
    nonExplicitSupportedLngs: true,
    resources: {
      it: { translation: it },
      en: { translation: en },
      ar: { translation: ar },
      fr: { translation: fr },
      de: { translation: de },
      es: { translation: es },
      zh: { translation: zh },
      ro: { translation: ro },
      uk: { translation: uk },
      ru: { translation: ru },
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'audiq-chat-lang',
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
  });

export default i18n;

// Mapping script → famiglia Noto + URL Google Fonts (CSS2).
// Sprint 3+: copre arabo, ebraico, CJK (4 varianti), devanagari, bengali, thai.
// I subset di Noto sono tutti caricati da Google Fonts CDN; in produzione
// (DEFER-005) faremo subsetting manuale + self-hosted.
const FONT_LOADERS: Record<string, { family: string; href: string; cssVariableStack: string }> = {
  arabic: {
    family: 'Noto Sans Arabic',
    href: 'https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;500;600;700&display=swap',
    cssVariableStack: "'Noto Sans Arabic', 'DM Sans', system-ui, sans-serif",
  },
  hebrew: {
    family: 'Noto Sans Hebrew',
    href: 'https://fonts.googleapis.com/css2?family=Noto+Sans+Hebrew:wght@400;500;600;700&display=swap',
    cssVariableStack: "'Noto Sans Hebrew', 'DM Sans', system-ui, sans-serif",
  },
  cjk_sc: {
    family: 'Noto Sans SC',
    href: 'https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;500;700&display=swap',
    cssVariableStack: "'Noto Sans SC', 'DM Sans', system-ui, sans-serif",
  },
  cjk_tc: {
    family: 'Noto Sans TC',
    href: 'https://fonts.googleapis.com/css2?family=Noto+Sans+TC:wght@400;500;700&display=swap',
    cssVariableStack: "'Noto Sans TC', 'DM Sans', system-ui, sans-serif",
  },
  cjk_jp: {
    family: 'Noto Sans JP',
    href: 'https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700&display=swap',
    cssVariableStack: "'Noto Sans JP', 'DM Sans', system-ui, sans-serif",
  },
  cjk_kr: {
    family: 'Noto Sans KR',
    href: 'https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;500;700&display=swap',
    cssVariableStack: "'Noto Sans KR', 'DM Sans', system-ui, sans-serif",
  },
  devanagari: {
    family: 'Noto Sans Devanagari',
    href: 'https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;500;700&display=swap',
    cssVariableStack: "'Noto Sans Devanagari', 'DM Sans', system-ui, sans-serif",
  },
  bengali: {
    family: 'Noto Sans Bengali',
    href: 'https://fonts.googleapis.com/css2?family=Noto+Sans+Bengali:wght@400;500;700&display=swap',
    cssVariableStack: "'Noto Sans Bengali', 'DM Sans', system-ui, sans-serif",
  },
  thai: {
    family: 'Noto Sans Thai',
    href: 'https://fonts.googleapis.com/css2?family=Noto+Sans+Thai:wght@400;500;700&display=swap',
    cssVariableStack: "'Noto Sans Thai', 'DM Sans', system-ui, sans-serif",
  },
  // Noto Sans (senza suffisso script) include il subset cirillico oltre a
  // latino+greco. Usato per UK/RU/BG/SR/MK/BE/MN — DM Sans non ha glifi cirillici.
  cyrillic: {
    family: 'Noto Sans',
    href: 'https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500;600;700&display=swap',
    cssVariableStack: "'Noto Sans', 'DM Sans', system-ui, sans-serif",
  },
};

// Heuristic: lingua → script bucket. Sprint 3 copre le lingue prioritarie e
// alcune varianti CJK comuni. Per lingua che non match cade su `null` (default).
function languageToFontKey(lang: string): keyof typeof FONT_LOADERS | null {
  const lower = lang.toLowerCase();
  if (RTL_LANGS.has(lower) && lower !== 'he') return 'arabic';
  if (lower === 'he') return 'hebrew';
  if (lower === 'zh' || lower.startsWith('zh-cn') || lower === 'zh-hans') return 'cjk_sc';
  if (lower.startsWith('zh-tw') || lower === 'zh-hant') return 'cjk_tc';
  if (lower === 'ja' || lower.startsWith('ja-')) return 'cjk_jp';
  if (lower === 'ko' || lower.startsWith('ko-')) return 'cjk_kr';
  if (lower === 'hi' || lower.startsWith('hi-') || lower === 'mr') return 'devanagari';
  if (lower === 'bn' || lower.startsWith('bn-')) return 'bengali';
  if (lower === 'th' || lower.startsWith('th-')) return 'thai';
  if (
    lower === 'uk' ||
    lower === 'ru' ||
    lower === 'bg' ||
    lower === 'be' ||
    lower === 'mk' ||
    lower === 'mn' ||
    lower === 'sr' ||
    lower.startsWith('uk-') ||
    lower.startsWith('ru-') ||
    lower.startsWith('sr-')
  ) {
    return 'cyrillic';
  }
  return null;
}

/**
 * Idempotente: carica il <link> Google Fonts per lo script richiesto se non
 * presente. Setta la variabile CSS `--font-sans` di conseguenza, e applica
 * `dir` + `lang` su <html>. Cache HTTP del browser previene il ri-fetch.
 */
export function applyDirAndFontForLang(lang: string): void {
  if (typeof document === 'undefined') return;
  const isRtl = RTL_LANGS.has(lang);
  document.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
  document.documentElement.setAttribute('lang', lang);

  const key = languageToFontKey(lang);
  const cfg = key ? FONT_LOADERS[key] : null;
  if (key && cfg) {
    const linkId = `audiq-noto-${key}`;
    if (!document.getElementById(linkId)) {
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = cfg.href;
      document.head.appendChild(link);
    }
    document.documentElement.style.setProperty('--font-sans', cfg.cssVariableStack);
  } else {
    document.documentElement.style.setProperty(
      '--font-sans',
      "'DM Sans', system-ui, -apple-system, sans-serif",
    );
  }
}
